import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import Layout from '../../../components/feature/Layout';
import PageBanner from '../../../components/feature/PageBanner';
import { Link } from 'react-router-dom';

const barnTypes = [
  {
    icon: 'ri-tractor-line',
    title: 'Agricultural Barns',
    desc: 'Spacious, durable barns engineered for livestock, grain storage, and all-weather farming operations across Michigan.',
    img: 'https://public.readdy.ai/ai/img_res/013aa1e8645f35d7c0f72015c9f1d2f4.jpg',
  },
  {
    icon: 'ri-car-line',
    title: 'Workshops & Garages',
    desc: 'Custom-built workshop and garage pole barns with insulation, electrical, and concrete flooring for the serious hobbyist or tradesperson.',
    img: 'https://readdy.ai/api/search-image?query=modern%20pole%20barn%20workshop%20garage%20with%20large%20metal%20doors%2C%20gray%20siding%2C%20concrete%20driveway%2C%20suburban%20property%2C%20clean%20professional%20construction%2C%20well%20lit%20exterior%20with%20clear%20blue%20sky&width=600&height=400&seq=pb02&orientation=landscape',
  },
  {
    icon: 'ri-home-4-line',
    title: 'Storage Structures',
    desc: 'Affordable, no-frills storage solutions built tough to protect equipment, vehicles, and materials from Michigan winters.',
    img: 'https://readdy.ai/api/search-image?query=clean%20steel%20metal%20storage%20pole%20barn%20building%20with%20brown%20siding%20and%20metal%20roof%2C%20gravel%20lot%2C%20rural%20Michigan%20setting%2C%20wide%20entrance%20doors%2C%20overcast%20sky%2C%20professional%20build%20quality&width=600&height=400&seq=pb03&orientation=landscape',
  },
];

const features = [
  'Steel post frame engineering', 'Custom dimensions & layouts', 'Insulated wall & roof options',
  'Concrete floor installation', 'Overhead & sliding doors', 'Electrical & lighting rough-in',
  'Metal roofing & siding', 'Skylights & ventilation', '30-year material warranty options', 'Permit assistance',
];

function FeatureItem({ text }: { text: string }) {
  return (
    <li className="flex items-center gap-3 group">
      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
        <i className="ri-checkbox-circle-fill text-primary text-base group-hover:text-primary-mid transition-colors duration-200"></i>
      </div>
      <span className="text-gray-700 text-sm">{text}</span>
    </li>
  );
}

export default function PoleBarnsPage() {
  const { ref: cardsRef, visible: cardsVis } = useScrollAnimation();
  const { ref: featRef, visible: featVis } = useScrollAnimation();

  return (
    <Layout>
      <PageBanner
        title="Pole Barn Construction"
        subtitle="Built tough for Michigan weather. Custom pole barns for agriculture, storage, workshops, and more — engineered to last generations."
        breadcrumb="Pole Barns"
        imageUrl="https://readdy.ai/api/search-image?query=large%20red%20and%20white%20pole%20barn%20building%20in%20rural%20Michigan%20farmland%2C%20dramatic%20wide%20angle%20view%2C%20green%20grass%2C%20bright%20blue%20sky%20with%20clouds%2C%20steel%20roof%2C%20professional%20construction%20company%20build%2C%20sunrise%20light&width=1440&height=600&seq=pbhero&orientation=landscape"
        accentWord="Construction"
      />

      {/* Intro */}
      <section className="bg-[#fafaf8] py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div ref={featRef} className={`transition-all duration-700 ${featVis ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-0.5 bg-primary" />
                <span className="text-primary text-xs font-bold uppercase tracking-[0.3em]">Why Downs Construction</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase text-gray-900 leading-tight tracking-tight mb-5 sm:mb-6" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                Built for <span className="text-primary">Michigan</span> Conditions
              </h2>
              <p className="text-gray-600 text-sm sm:text-base leading-relaxed mb-5 sm:mb-6">
                Pole barns are one of the most cost-effective and versatile structures you can build. At Downs Construction, we've been engineering post-frame buildings that handle the harshest Michigan winters since 1992. Every build is designed around your specific needs — size, use, budget.
              </p>
              <p className="text-gray-600 text-sm sm:text-base leading-relaxed mb-7 sm:mb-8">
                From permits to final inspection, we manage the entire project so you don't have to stress about a thing.
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {features.map((f) => <FeatureItem key={f} text={f} />)}
              </ul>
            </div>
            <div className={`transition-all duration-700 delay-200 ${featVis ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
              <div className="grid grid-cols-2 gap-4">
                <div className="w-full h-56 sm:h-64 rounded-lg overflow-hidden col-span-2 img-zoom">
                  <img src="https://readdy.ai/api/search-image?query=aerial%20view%20of%20large%20brown%20pole%20barn%20being%20constructed%20on%20farmland%2C%20Michigan%20aerial%20photography%2C%20steel%20frame%20post%20construction%2C%20blue%20sky%2C%20green%20grass%20surrounding%20property%2C%20workers%20on%20site&width=700&height=350&seq=pb04&orientation=landscape" alt="Pole barn construction" className="w-full h-full object-cover object-top" />
                </div>
                <div className="w-full h-40 sm:h-44 rounded-lg overflow-hidden img-zoom">
                  <img src="https://readdy.ai/api/search-image?query=interior%20of%20finished%20pole%20barn%20workshop%20with%20concrete%20floor%2C%20insulated%20walls%2C%20overhead%20lighting%2C%20large%20metal%20garage%20door%20open%2C%20clean%20and%20spacious%20interior%2C%20neutral%20gray%20colors&width=350&height=250&seq=pb05&orientation=landscape" alt="Pole barn interior" className="w-full h-full object-cover object-top" />
                </div>
                <div className="w-full h-40 sm:h-44 rounded-lg overflow-hidden bg-gradient-to-br from-primary to-primary-mid flex flex-col items-center justify-center p-5 text-center">
                  <div className="text-4xl sm:text-5xl font-black text-white mb-1" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>50+</div>
                  <div className="text-primary-light text-xs sm:text-sm font-bold uppercase tracking-wider">Pole Barns Built</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Types */}
      <section className="bg-primary-dark py-16 sm:py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 80% 50%, #2A5BA8 0%, transparent 50%)' }} />
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-16 relative z-10">
          <div className="text-center mb-12 sm:mb-14">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="w-8 h-0.5 bg-primary-light" />
              <span className="text-primary-light text-xs font-bold uppercase tracking-[0.3em]">What We Build</span>
              <div className="w-8 h-0.5 bg-primary-light" />
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase text-white tracking-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Pole Barn <span className="text-primary-light">Types</span>
            </h2>
          </div>
          <div ref={cardsRef} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5 sm:gap-6">
            {barnTypes.map((barn, i) => (
              <div
                key={barn.title}
                className="bg-white/5 border border-white/10 rounded-lg overflow-hidden hover-card hover:border-primary-mid/40"
                style={{ opacity: cardsVis ? 1 : 0, transform: cardsVis ? 'translateY(0)' : 'translateY(32px)', transition: `all 0.7s ease ${i * 130}ms` }}
              >
                <div className="w-full h-44 sm:h-48 overflow-hidden img-zoom">
                  <img src={barn.img} alt={barn.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-5 sm:p-6">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary/40 rounded-lg mb-4">
                    <i className={`${barn.icon} text-primary-light text-xl`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-white uppercase tracking-wide mb-3" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{barn.title}</h3>
                  <p className="text-blue-200/60 text-sm leading-relaxed">{barn.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary to-primary-mid">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase text-white mb-4 tracking-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            Start Your Pole Barn Project
          </h2>
          <p className="text-blue-100/80 mb-8 text-sm sm:text-base max-w-xl mx-auto">Get a free quote with no obligation. We'll design the perfect structure for your property and budget.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-white text-primary font-bold uppercase tracking-widest text-sm px-8 py-4 rounded-md hover:bg-primary-light transition-colors duration-200 cursor-pointer whitespace-nowrap btn-press">
              Get Free Quote
            </Link>
            <a href="tel:269-599-4913" className="border-2 border-white/60 text-white font-bold uppercase tracking-widest text-sm px-8 py-4 rounded-md hover:bg-white/10 transition-colors duration-200 cursor-pointer whitespace-nowrap btn-press">
              (269) 599-4913
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
}
